import type { CategoryTotalDto, DashboardDto, TransactionDto } from "@home/shared";
import { useState } from "react";
import { api } from "../api.ts";
import { AccountSheet } from "../components/AccountSheet.tsx";
import { AccountRows, AccountsSheet } from "../components/AccountsSheet.tsx";
import type { TxType } from "../components/AddTxSheet.tsx";
import { DailyChart } from "../components/DailyChart.tsx";
import { DebtsSummaryCard } from "../components/Debts.tsx";
import { EmptyState, SkeletonCard } from "../components/EmptyState.tsx";
import { InsightCard } from "../components/InsightCard.tsx";
import { PricesCard } from "../components/Prices.tsx";
import { Sheet } from "../components/Sheet.tsx";
import { TxDetailSheet } from "../components/TxDetailSheet.tsx";
import { TxList } from "../components/TxList.tsx";
import { changePct, currentMonth, money, monthTitle, shiftMonth } from "../format.ts";
import { t, type Lang } from "../i18n.ts";
import { useSync } from "../store.ts";
import { useRemote } from "../useRemote.ts";

type Drill = { kind: "category"; category: CategoryTotalDto } | null;

interface Props {
  lang: Lang;
  onBack: () => void;
  onAdd: (type: TxType) => void;
  onOpenOps: (month: string) => void;
  onOpenDebts: () => void;
}

/** Отчёт за месяц: итоги, на что уходят деньги, советы ИИ, графики, счета и долги. */
export function ReportPage({ lang, onBack, onAdd, onOpenOps, onOpenDebts }: Props) {
  const { status } = useSync();
  const online = status === "online" || status === "syncing";
  const [month, setMonth] = useState(currentMonth());
  const remote = useRemote(`dashboard:${month}`, () => api.dashboard(month));
  const [drill, setDrill] = useState<Drill>(null);
  const [addingAccount, setAddingAccount] = useState(false);
  const d = remote.data;
  const isCurrent = month === currentMonth();

  return (
    <main className="dash">
      <div className="list-header">
        <button className="icon-btn back-btn" onClick={onBack} aria-label="back">
          ←
        </button>
        <h2 className="page-heading">📊 {t("reportTitle")}</h2>
      </div>
      <header className="month-switch">
        <button className="icon-btn" onClick={() => setMonth(shiftMonth(month, -1))} aria-label="prev">
          ‹
        </button>
        <h2>{monthTitle(month, lang)}</h2>
        <button className="icon-btn" disabled={isCurrent} onClick={() => setMonth(shiftMonth(month, 1))} aria-label="next">
          ›
        </button>
      </header>

      {remote.staleSince && (
        <div className="banner banner-warn">
          {t("offlineData", { time: new Date(remote.staleSince).toLocaleString(lang === "uz" ? "uz-UZ" : "ru-RU", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" }) })}
        </div>
      )}
      {!d && remote.loading && (
        <>
          <SkeletonCard lines={3} />
          <SkeletonCard lines={2} />
          <SkeletonCard lines={4} />
        </>
      )}
      {!d && !remote.loading && remote.error != null && <p className="empty">{t("loadError")}</p>}

      {d && (
        <>
          <Totals d={d} lang={lang} />

          {d.totals.expense === 0 && d.totals.income === 0 ? (
            <section className="dash-card">
              <EmptyState icon="📊" title={t("noOps")} text={t("noData")} action={{ label: `＋ ${t("firstOp")}`, onClick: () => onAdd("expense") }} />
            </section>
          ) : (
            <Categories d={d} lang={lang} onPick={(category) => setDrill({ kind: "category", category })} />
          )}
          {isCurrent && <InsightCard lang={lang} online={online} />}
          {isCurrent && <PricesCard lang={lang} />}
          {d.daily.some((x) => x.expense > 0) && (
            <section className="dash-card">
              <h3>{t("dailyTitle")}</h3>
              <DailyChart key={d.month} days={d.daily} lang={lang} />
            </section>
          )}
          <TopLists d={d} lang={lang} />
          <Accounts d={d} lang={lang} onAdd={() => setAddingAccount(true)} />
          {isCurrent && d.debts && <DebtsSummaryCard s={d.debts} lang={lang} onOpen={onOpenDebts} />}
          <button className="btn btn-block btn-surface" onClick={() => onOpenOps(month)}>
            {t("allOps")} ›
          </button>
        </>
      )}

      {drill && <DrillSheet month={month} drill={drill} lang={lang} onClose={() => setDrill(null)} />}
      {addingAccount && <AccountsSheet lang={lang} startAdding onClose={() => setAddingAccount(false)} />}
    </main>
  );
}

function Delta({ current, previous, inverse }: { current: number; previous: number; inverse?: boolean }) {
  const pct = changePct(current, previous);
  if (pct === null || pct === 0) return null;
  // Для расходов рост — плохо, для доходов — хорошо.
  const good = inverse ? pct < 0 : pct > 0;
  return (
    <span className={good ? "delta good" : "delta bad"}>
      {pct > 0 ? "↑" : "↓"} {Math.abs(pct)}%
    </span>
  );
}

function Totals({ d, lang }: { d: DashboardDto; lang: Lang }) {
  const { totals } = d;
  return (
    <section className="dash-card totals">
      <div className="hero">
        <div className="hero-label">{t("balanceTotal")}</div>
        <div className="hero-value">{money(d.totalBalanceUzs, "UZS", lang)}</div>
      </div>
      <div className="stat-row">
        <div className="stat">
          <div className="stat-label">{t("incomeL")}</div>
          <div className="stat-value income">{money(totals.income, "UZS", lang)}</div>
          <Delta current={totals.income} previous={totals.prevIncome} />
        </div>
        <div className="stat">
          <div className="stat-label">{t("expenseL")}</div>
          <div className="stat-value">{money(totals.expense, "UZS", lang)}</div>
          <Delta current={totals.expense} previous={totals.prevExpense} inverse />
        </div>
      </div>
      {(d.hiddenPersonalIncome > 0 || d.hiddenPersonalExpense > 0) && (
        <div className="hint cmp-hint">
          🔒{" "}
          {t("hiddenInTotals", {
            parts: [
              d.hiddenPersonalIncome > 0 ? `${t("incomeL").toLowerCase()} ${money(d.hiddenPersonalIncome, "UZS", lang, true)}` : null,
              d.hiddenPersonalExpense > 0 ? `${t("expenseL").toLowerCase()} ${money(-d.hiddenPersonalExpense, "UZS", lang)}` : null,
            ]
              .filter(Boolean)
              .join(", "),
          })}
        </div>
      )}
      {(totals.prevIncome > 0 || totals.prevExpense > 0) && (
        <div className="hint cmp-hint">{d.comparedDays ? t("cmpPartial", { d: d.comparedDays }) : t("cmpFull")}</div>
      )}
      {totals.savingsRate !== null && (
        <div className={totals.net >= 0 ? "saving good" : "saving bad"}>
          {totals.net >= 0 ? `✅ ${t("savedShare", { p: Math.round(totals.savingsRate * 100) })}` : `⚠️ ${t("overspent")}`}
          <b>{money(totals.net, "UZS", lang, true)}</b>
        </div>
      )}
    </section>
  );
}

function Accounts({ d, lang, onAdd }: { d: DashboardDto; lang: Lang; onAdd: () => void }) {
  const [opened, setOpened] = useState<string | null>(null);
  // Заначка — отдельно: в общий остаток она не входит.
  const stashes = d.accounts.filter((a) => a.stash);
  return (
    <section className="dash-card">
      <div className="dash-card-head">
        <h3>💳 {t("accountsTitle")}</h3>
        <button type="button" className="btn-small-ghost" onClick={onAdd}>
          ＋ {t("addAccount")}
        </button>
      </div>
      <AccountRows accounts={d.accounts.filter((a) => !a.stash)} lang={lang} onPick={setOpened} />
      {stashes.length > 0 && (
        <>
          <h3 className="sub-heading">{t("stashSection")}</h3>
          <AccountRows accounts={stashes} lang={lang} onPick={setOpened} />
        </>
      )}
      {d.usdRate && <div className="hint">{t("usdRate", { rate: money(d.usdRate, "UZS", lang) })}</div>}
      {d.fxMissing && <div className="hint">{t("fxMissing")}</div>}
      {opened && <AccountSheet id={opened} lang={lang} onClose={() => setOpened(null)} />}
    </section>
  );
}

function Categories({ d, lang, onPick }: { d: DashboardDto; lang: Lang; onPick: (c: CategoryTotalDto) => void }) {
  if (d.categories.length === 0 && d.hiddenPersonalExpense === 0) return null;
  const max = Math.max(...d.categories.map((c) => c.amount), 1);
  return (
    <section className="dash-card">
      <h3>{t("categoriesTitle")}</h3>
      <ul className="cat-list">
        {d.categories.map((c) => {
          const pct = changePct(c.amount, c.prevAmount);
          return (
            <li key={c.code}>
              <button className="cat-row" onClick={() => onPick(c)}>
                <span className="cat-icon">{c.icon}</span>
                <span className="cat-main">
                  <span className="cat-line">
                    <span className="cat-name">{c.name}</span>
                    <span className="cat-amount">{money(c.amount, "UZS", lang)}</span>
                  </span>
                  <span className="cat-bar">
                    <span style={{ width: `${(c.amount / max) * 100}%` }} />
                  </span>
                  <span className="cat-meta">
                    {Math.round(c.share * 100)}% · {t("times", { n: c.count })}
                    {pct !== null && pct !== 0 && (
                      <span className={pct > 0 ? "delta bad" : "delta good"}>
                        {" "}
                        {pct > 0 ? "↑" : "↓"}
                        {Math.abs(pct)}%
                      </span>
                    )}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {d.hiddenPersonalExpense > 0 && (
        <div className="hint">
          🔒 {t("hiddenPersonal")}: {money(d.hiddenPersonalExpense, "UZS", lang)}
        </div>
      )}
    </section>
  );
}

function TopLists({ d, lang }: { d: DashboardDto; lang: Lang }) {
  return (
    <>
      {d.topPlaces.length > 0 && (
        <section className="dash-card">
          <h3>📍 {t("topPlaces")}</h3>
          <ol className="rank-list">
            {d.topPlaces.map((p) => (
              <li key={p.name}>
                <span>
                  {p.name} <span className="muted">· {t("times", { n: p.count })}</span>
                </span>
                <b>{money(p.amount, "UZS", lang)}</b>
              </li>
            ))}
          </ol>
        </section>
      )}
      {d.topItems.length > 0 && (
        <section className="dash-card">
          <h3>🧺 {t("topItems")}</h3>
          <ol className="rank-list">
            {d.topItems.map((p) => (
              <li key={p.name}>
                <span>
                  {p.name} <span className="muted">· {t("times", { n: p.count })}</span>
                </span>
                <b>{money(p.amount, "UZS", lang)}</b>
              </li>
            ))}
          </ol>
        </section>
      )}
    </>
  );
}

function DrillSheet({ month, drill, lang, onClose }: { month: string; drill: NonNullable<Drill>; lang: Lang; onClose: () => void }) {
  const { category } = drill;
  const remote = useRemote(`tx:${month}:${category.code}`, () => api.transactions(month, category.code).then((r) => r.transactions));
  const [picked, setPicked] = useState<TransactionDto | null>(null);
  return (
    <Sheet title={`${category.icon} ${category.name}`} onClose={onClose}>
      <p className="hint">
        {money(category.amount, "UZS", lang)} · {Math.round(category.share * 100)}% · {t("times", { n: category.count })}
      </p>
      {remote.data ? <TxList items={remote.data} lang={lang} onPick={setPicked} /> : <div className="skeleton skeleton-block" />}
      {picked && <TxDetailSheet tx={picked} lang={lang} onClose={() => setPicked(null)} />}
    </Sheet>
  );
}
